import { Tooltip } from 'antd';
import { startCase } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import { Task } from 'api/tasks';
import { Radio } from 'components/atoms/radio';
import {
  Content,
  FieldLabel,
  GhostFields,
  Help,
  LabelContainer,
  Left,
  Right,
  Row,
} from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { SingleSelectField } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields';
import {
  hasReadAccessForField,
  hasWriteAccessForField,
} from 'utils/field-access';
import { getRandomColor } from 'utils/get-random-color';
import { useTimeEntryField } from './use-time-entry-field';

type TaskBase = Omit<
  Task,
  'startDate' | 'dueDate' | 'dependentTasks' | 'subTasks' | 'project'
>;

export const TimeEntrySettingList: FC<{
  task: TaskBase;
  onUpdateTask(task: TaskBase): void;
  disabled?: boolean;
}> = ({ task, onUpdateTask, disabled = false }) => {
  const params = useParams<{ templateId?: string }>();
  const isTemplatePage = !!params?.templateId;

  const {
    billableField,
    categoryField,
    defaultBillable,
    defaultCategory,
    onFieldValueChange,
  } = useTimeEntryField(task, onUpdateTask);

  return (
    <GhostFields>
      {billableField != null &&
      (isTemplatePage ||
        hasReadAccessForField(billableField, task.fieldsAccessMetaInBase3)) ? (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                {billableField.fieldLabel ?? startCase(billableField.fieldName)}
                {billableField.description ? (
                  <Tooltip title={billableField.description}>
                    <Help />
                  </Tooltip>
                ) : null}
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right>
            <Content>
              <Radio.Group
                style={{ marginLeft: 14 }}
                disabled={
                  disabled ||
                  (!isTemplatePage &&
                    !hasWriteAccessForField(
                      billableField,
                      task.fieldsAccessMetaInBase3,
                    ))
                }
                value={defaultBillable ?? true}
                onChange={(e) => {
                  onFieldValueChange({
                    defaultTimeEntryBillable: e.target.value,
                  });
                }}
              >
                <Radio value>
                  <FormattedMessage id="yes" />
                </Radio>
                <Radio value={false}>
                  <FormattedMessage id="no" />
                </Radio>
              </Radio.Group>
            </Content>
          </Right>
        </Row>
      ) : null}
      {categoryField != null &&
      (isTemplatePage ||
        hasReadAccessForField(categoryField, task.fieldsAccessMetaInBase3)) ? (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                {categoryField.fieldLabel ?? startCase(categoryField.fieldName)}
                {categoryField.description ? (
                  <Tooltip title={categoryField.description}>
                    <Help />
                  </Tooltip>
                ) : null}
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right>
            <Content>
              <SingleSelectField
                disabled={
                  disabled ||
                  (!isTemplatePage &&
                    !hasWriteAccessForField(
                      categoryField,
                      task.fieldsAccessMetaInBase3,
                    ))
                }
                field={
                  {
                    ...categoryField,
                    metaData: {
                      ...categoryField.metaData,
                      options: (
                        (categoryField.metaData as any)?.options ?? []
                      ).map((x) => ({ ...x, color: getRandomColor(x.value) })),
                    },
                    value: defaultCategory ?? null,
                  } as any
                }
                onChange={(selected) =>
                  onFieldValueChange({
                    defaultTimeEntryCategory: selected,
                  })
                }
              />
            </Content>
          </Right>
        </Row>
      ) : null}
    </GhostFields>
  );
};
